import type { ComponentType } from "react";
import type { CompanyDomain } from "src/common/company-domain/types.js";
import type { LegalEntity } from "src/common/service/nexus/endpoints/companyDomain/types.js";
import { AiChatWidget } from "./AiChatWidget.js";
import { ExcelReportsWidget } from "./ExcelReportsWidget.js";
import { ExopenProductsWidget } from "./ExopenProductsWidget.js";
import { IntegrationStatusWidget } from "./IntegrationStatusWidget.js";
import { NewsWidget } from "./NewsWidget.js";
import { PowerBiWidget } from "./PowerBiWidget.js";
import { ReportsWidget } from "./ReportsWidget.js";

/**
 * Stable identifier for a dashboard widget. Once a per-user layout
 * preference exists (add/remove/reorder/resize — see useProfilePreference.ts
 * and useCollapsibleSidebar.tsx for the established local-first + remote
 * sync pattern this will use), it will be keyed by this id, so an id must
 * never be renamed or reused for a different widget once shipped.
 */
export type DashboardWidgetId =
	| "ai-chat"
	| "products"
	| "integration-status"
	| "reports"
	| "excel-reports"
	| "powerbi-reports"
	| "news"
	| "revenue-by-company";

/** Everything a widget may need to decide whether it will show anything —
 * all synchronously available from route loader data, so the dashboard can
 * count its visible widgets BEFORE rendering and derive the single parity
 * filler from that count. */
export interface DashboardWidgetContext {
	companyDomain: CompanyDomain;
	legalEntities: LegalEntity[];
	isSuperAdmin: boolean;
	devToolsUnlocked: boolean;
	/** From `useHasExcelReportAccess`, which needs hooks the registry can't call. */
	hasExcelReportAccess: boolean;
	isCompanyAdmin: boolean;
	/** From the Power BI connection status, which needs hooks the registry can't call. */
	powerBiConnected: boolean;
	legalEntityId: string | undefined;
}

interface DashboardWidgetEntry {
	id: DashboardWidgetId;
	Component: ComponentType;
	/** Omitted = always visible. A widget must never render an empty box or
	 * its own filler; if it can have nothing to show, it says so here so the
	 * dashboard's widget count (and thus the filler) stays correct. */
	isVisible?: (context: DashboardWidgetContext) => boolean;
}

/**
 * The dashboard's widgets, in default order. StartDashboard filters this by
 * `isVisible` and renders the result into a two-column grid; an odd count
 * simply leaves the last cell empty. A future per-user layout preference
 * only has to filter/reorder this array.
 *
 * RevenueByCompanyWidget is dev-tools-gated (unreleased) and rendered
 * lazily by StartDashboard, which is why it isn't imported here — but it IS
 * counted here, via `revenueByCompanyVisible`.
 */
// Hidden at company scope: a per-company breakdown of one company says nothing.
export const revenueByCompanyVisible = (context: DashboardWidgetContext) =>
	context.devToolsUnlocked &&
	context.legalEntities.length > 0 &&
	!context.legalEntityId;

// Dev-tools gated: the report-database module source it's built on
// (useGetReportStatusQuery) hasn't had its edge cases verified yet — see
// openspec/changes/report-modules-endpoint-migration (portal#4301).
// Also hidden at company scope: reportModules carry no legal entity, so there is nothing to scope to (portal#4301).
const reportsWidgetVisible = (context: DashboardWidgetContext) =>
	context.devToolsUnlocked && !context.legalEntityId;

// WHY admins before a connection: only they can connect, so it would just be noise for everyone else.
export const powerBiWidgetVisible = (context: DashboardWidgetContext) =>
	context.isCompanyAdmin || context.powerBiConnected;

export const DASHBOARD_WIDGETS: DashboardWidgetEntry[] = [
	// Shown at company scope too: hiding it reads as "not available to you".
	{ id: "ai-chat", Component: AiChatWidget },
	{ id: "products", Component: ExopenProductsWidget },
	{ id: "integration-status", Component: IntegrationStatusWidget },
	{ id: "news", Component: NewsWidget },
	{ id: "reports", Component: ReportsWidget, isVisible: reportsWidgetVisible },
	{
		id: "excel-reports",
		Component: ExcelReportsWidget,
		// Its documents/me call provisions an eox7 report user for everyone this shows to, admins included.
		isVisible: (context) => context.hasExcelReportAccess,
	},
	{
		id: "powerbi-reports",
		Component: PowerBiWidget,
		isVisible: powerBiWidgetVisible,
	},
];
